import { useState, lazy, Suspense } from "react";
import { API } from "../../App";

const RichTextEditor = lazy(() => import("../RichTextEditor"));

export default function AdminBlogTab({ 
  blogPosts, 
  adminKey, 
  onRefresh 
}) {
  const [showBlogModal, setShowBlogModal] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [blogForm, setBlogForm] = useState({
    title: "",
    slug: "",
    content: "",
    excerpt: "",
    featured_image: "",
    category: "",
    tags: "",
    is_published: false,
    scheduled_at: ""
  });

  const openCreateModal = () => {
    setBlogForm({
      title: "", slug: "", content: "", excerpt: "",
      featured_image: "", category: "", tags: "", is_published: false, scheduled_at: ""
    });
    setShowBlogModal({ mode: "create" });
  };

  const openEditModal = (post) => {
    setBlogForm({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || "",
      featured_image: post.featured_image || "",
      category: post.category || "",
      tags: post.tags?.join(", ") || "",
      is_published: post.is_published,
      scheduled_at: post.scheduled_at || ""
    });
    setShowBlogModal({ mode: "edit", post_id: post.post_id });
  };

  const saveBlogPost = async () => {
    const payload = {
      ...blogForm,
      tags: blogForm.tags.split(",").map(t => t.trim()).filter(Boolean),
      scheduled_at: blogForm.scheduled_at || null
    };
    
    try {
      if (showBlogModal.mode === "create") {
        const response = await fetch(`${API}/admin/blog/posts?admin_key=${adminKey}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const error = await response.json();
          alert(error.detail || "Failed to create post");
          return;
        }
      } else {
        await fetch(`${API}/admin/blog/posts/${showBlogModal.post_id}?admin_key=${adminKey}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
      }
      setShowBlogModal(null);
      onRefresh();
    } catch (err) {
      alert("Failed to save post");
    }
  };

  const deleteBlogPost = async (postId) => {
    if (!confirm("Delete this blog post and all its comments?")) return;
    try {
      await fetch(`${API}/admin/blog/posts/${postId}?admin_key=${adminKey}`, { method: "DELETE" });
      onRefresh();
    } catch (err) {
      alert("Failed to delete post");
    }
  };

  const uploadBlogImage = async (file) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/admin/blog/upload-image?admin_key=${adminKey}`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const result = await response.json();
      setBlogForm(prev => ({ ...prev, featured_image: result.url }));
    } catch (err) {
      alert('Failed to upload image: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      const params = new URLSearchParams({
        admin_key: adminKey,
        title: blogForm.title || "Sample Blog Post",
        content: blogForm.content || "",
        excerpt: blogForm.excerpt || "",
        category: blogForm.category || "",
        slug: blogForm.slug || "test-post"
      });
      const response = await fetch(`${API}/admin/blog/test-email?${params}`, { method: "POST" });
      const result = await response.json();
      if (response.ok) {
        alert(result.message);
      } else {
        alert(result.detail || "Failed to send test email");
      }
    } catch (err) {
      alert("Failed to send test email");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-medium text-[#2D2A26]">Blog Posts</h2>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] text-sm"
          data-testid="create-blog-post-btn"
        >
          + New Post
        </button>
      </div>

      {/* Posts List */}
      <div className="space-y-4">
        {blogPosts.map(post => (
          <div key={post.post_id} className="bg-white rounded-xl border border-[#E8E4DE] p-5">
            <div className="flex items-start gap-4">
              {/* Thumbnail */}
              {post.featured_image ? (
                <img src={post.featured_image} alt="" className="w-24 h-16 object-cover rounded-lg flex-shrink-0" />
              ) : (
                <div className="w-24 h-16 bg-[#F5F2EE] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#A8A4A0]">✧</span>
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-[#2D2A26] truncate">{post.title}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    post.is_published 
                      ? "bg-green-100 text-green-700" 
                      : post.scheduled_at
                        ? "bg-blue-100 text-blue-700"
                        : "bg-yellow-100 text-yellow-700"
                  }`}>
                    {post.is_published ? "Published" : post.scheduled_at ? "Scheduled" : "Draft"}
                  </span>
                </div>
                <p className="text-sm text-[#6E6A66] mb-2">/{post.slug}</p>
                <div className="flex items-center gap-4 text-xs text-[#A8A4A0]">
                  {post.category && <span className="capitalize">{post.category}</span>}
                  <span>{post.view_count || 0} views</span>
                  {post.scheduled_at && !post.is_published && (
                    <span className="text-blue-600">Scheduled: {new Date(post.scheduled_at).toLocaleString()}</span>
                  )}
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(post)}
                  className="px-3 py-1.5 text-sm border border-[#E8E4DE] rounded-lg hover:bg-[#F5F2EE]"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteBlogPost(post.post_id)}
                  className="px-3 py-1.5 text-sm text-red-500 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {blogPosts.length === 0 && (
          <div className="text-center py-12 text-[#6E6A66]">
            No blog posts yet. Create your first post to get started.
          </div>
        )}
      </div>

      {/* Blog Post Modal */}
      {showBlogModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-[#2D2A26] mb-4">
              {showBlogModal.mode === "create" ? "Create Blog Post" : "Edit Blog Post"}
            </h3>
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Title</label>
                <input
                  type="text"
                  value={blogForm.title}
                  onChange={(e) => setBlogForm({...blogForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  placeholder="Article title"
                />
              </div>
              
              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">URL Slug</label>
                <div className="flex items-center">
                  <span className="text-sm text-[#A8A4A0] mr-2">/blog/</span>
                  <input
                    type="text"
                    value={blogForm.slug}
                    onChange={(e) => setBlogForm({...blogForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})}
                    className="flex-1 px-3 py-2 border border-[#E8E4DE] rounded-lg"
                    placeholder="article-url-slug"
                  />
                </div>
              </div>

              {/* Content (Rich Text) */}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Content</label>
                <Suspense fallback={<div className="p-4 border border-[#E8E4DE] rounded-lg text-center text-[#6E6A66]">Loading editor...</div>}>
                  <RichTextEditor
                    value={blogForm.content}
                    onChange={(content) => setBlogForm({...blogForm, content})}
                    placeholder="Write your article content here..."
                  />
                </Suspense>
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Excerpt (optional)</label>
                <textarea
                  value={blogForm.excerpt}
                  onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg resize-none"
                  placeholder="Short preview text for listings..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Featured Image */}
                <div>
                  <label className="block text-sm font-medium text-[#6E6A66] mb-1">Featured Image</label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files[0] && uploadBlogImage(e.target.files[0])}
                        className="hidden"
                        id="blog-image-upload"
                      />
                      <label
                        htmlFor="blog-image-upload"
                        className={`flex-1 px-3 py-2 border border-[#E8E4DE] rounded-lg text-center cursor-pointer hover:bg-[#F5F2EE] text-sm ${uploadingImage ? 'opacity-50' : ''}`}
                      >
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      </label>
                    </div>
                    <input
                      type="url"
                      value={blogForm.featured_image}
                      onChange={(e) => setBlogForm({...blogForm, featured_image: e.target.value})}
                      className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg text-sm"
                      placeholder="Or paste URL..."
                    />
                    {blogForm.featured_image && (
                      <img src={blogForm.featured_image.startsWith('/api') ? `${API.replace('/api', '')}${blogForm.featured_image}` : blogForm.featured_image} alt="Preview" className="w-full h-24 object-cover rounded-lg" />
                    )}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-[#6E6A66] mb-1">Category</label>
                  <input
                    type="text"
                    value={blogForm.category}
                    onChange={(e) => setBlogForm({...blogForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                    placeholder="e.g., wellness, rituals"
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Tags (comma separated)</label>
                <input
                  type="text"
                  value={blogForm.tags}
                  onChange={(e) => setBlogForm({...blogForm, tags: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  placeholder="self-care, meditation, journaling"
                />
              </div>

              {/* Publishing Options */}
              <div className="bg-[#F9F7F4] rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-[#2D2A26]">Publishing</p>
                
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="publish_option"
                    checked={!blogForm.is_published && !blogForm.scheduled_at}
                    onChange={() => setBlogForm({...blogForm, is_published: false, scheduled_at: ""})}
                  />
                  <span className="text-sm text-[#6E6A66]">Save as draft</span>
                </label>
                
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="publish_option"
                    checked={blogForm.is_published}
                    onChange={() => setBlogForm({...blogForm, is_published: true, scheduled_at: ""})}
                  />
                  <span className="text-sm text-[#6E6A66]">Publish immediately</span>
                </label>
                
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="publish_option"
                    checked={!blogForm.is_published && !!blogForm.scheduled_at}
                    onChange={() => setBlogForm({...blogForm, is_published: false, scheduled_at: new Date(Date.now() + 86400000).toISOString().slice(0, 16)})}
                  />
                  <span className="text-sm text-[#6E6A66]">Schedule for later</span>
                </label>
                
                {!blogForm.is_published && blogForm.scheduled_at && (
                  <input
                    type="datetime-local"
                    value={blogForm.scheduled_at.slice(0, 16)}
                    onChange={(e) => setBlogForm({...blogForm, scheduled_at: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg text-sm"
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              {/* Test Email Button */}
              <button
                onClick={sendTestEmail}
                disabled={!blogForm.title}
                className="w-full px-4 py-2 border border-[#A87C6D] text-[#A87C6D] rounded-lg hover:bg-[#A87C6D]/10 disabled:opacity-50 text-sm"
              >
                📧 Send Test Email to Admin
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBlogModal(null)}
                  className="flex-1 px-4 py-2 border border-[#E8E4DE] rounded-lg hover:bg-[#F5F2EE]"
                >
                  Cancel
                </button>
                <button
                  onClick={saveBlogPost}
                  disabled={!blogForm.title || !blogForm.slug || !blogForm.content}
                  className="flex-1 px-4 py-2 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] disabled:opacity-50"
                >
                  {showBlogModal.mode === "create" ? "Create Post" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
